import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Pencil, KeyRound, ListChecks, Trash2, Power } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { ROLE_LABELS, errMsg } from './consoleUtils';
import { AccountFormDialog } from './AccountFormDialog';
import { CredentialsDialog } from './CredentialsDialog';

const IconBtn = ({ icon: Icon, title, onClick, testid, danger }) => (
  <button title={title} onClick={onClick} data-testid={testid}
    className={`p-1.5 rounded-md transition-colors ${danger ? 'text-destructive hover:bg-destructive/10' : 'text-muted-foreground hover:text-primary hover:bg-muted'}`}>
    <Icon className="w-4 h-4" />
  </button>
);

export const AccountsTab = ({ meta, accounts, reload, onPermissions }) => {
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creds, setCreds] = useState(null);

  const rows = accounts.filter(a => `${a.full_name} ${a.email} ${a.role} ${a.department}`.toLowerCase().includes(search.toLowerCase()));

  const submit = async (data) => {
    try {
      if (editing) {
        await axios.put(`/api/console/users/${editing.id}`, data);
        toast.success('Account updated');
      } else {
        const r = await axios.post('/api/console/users', data);
        setCreds({ title: 'Account created', email: r.data.user.email, password: r.data.temp_password });
      }
      setFormOpen(false);
      reload();
    } catch (e) { toast.error(errMsg(e, 'Could not save account')); }
  };

  const resetPassword = async (a) => {
    if (!window.confirm(`Reset password for ${a.full_name}? Their current password will stop working.`)) return;
    try {
      const r = await axios.post(`/api/console/users/${a.id}/reset-password`, {});
      setCreds({ title: 'Password reset', email: r.data.email, password: r.data.temp_password });
      reload();
    } catch (e) { toast.error(errMsg(e, 'Reset failed')); }
  };

  const toggleActive = async (a) => {
    try {
      await axios.put(`/api/console/users/${a.id}`, { is_active: !a.is_active });
      toast.success(a.is_active ? 'Account disabled' : 'Account enabled');
      reload();
    } catch (e) { toast.error(errMsg(e, 'Update failed')); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete ${a.full_name}'s account permanently?`)) return;
    try { await axios.delete(`/api/console/users/${a.id}`); toast.success('Account deleted'); reload(); }
    catch (e) { toast.error(errMsg(e, 'Delete failed')); }
  };

  return (
    <Card className="border-border/40" data-testid="console-accounts-tab">
      <CardContent className="p-4 sm:p-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search accounts" value={search} onChange={e => setSearch(e.target.value)} data-testid="input-search-accounts" />
          </div>
          <Button size="sm" onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="btn-create-account">
            <Plus className="w-4 h-4 mr-1" /> Create account
          </Button>
        </div>
        <p className="text-xs text-muted-foreground" data-testid="accounts-count">{accounts.length} account(s)</p>
        <div className="divide-y divide-border/40 border border-border/40 rounded-md">
          {rows.map(a => (
            <div key={a.id} className={`p-3 flex flex-wrap items-center gap-3 ${a.is_active ? '' : 'opacity-60'}`} data-testid={`account-row-${a.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="font-medium text-sm text-foreground flex items-center gap-2 flex-wrap">
                  {a.full_name}
                  {!a.is_active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-destructive/10 text-destructive">Disabled</span>}
                  {a.must_change_password && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Temp password</span>}
                </div>
                <div className="text-xs text-muted-foreground break-all">{a.email}</div>
              </div>
              <div className="text-xs text-muted-foreground w-40">
                <div className="font-medium text-foreground">{ROLE_LABELS[a.role] || a.role}</div>
                <div>{a.department || '-'}</div>
              </div>
              <div className="flex items-center gap-0.5">
                <IconBtn icon={Pencil} title="Edit" onClick={() => { setEditing(a); setFormOpen(true); }} testid={`btn-edit-account-${a.id}`} />
                <IconBtn icon={ListChecks} title="Permissions" onClick={() => onPermissions(a.id)} testid={`btn-permissions-account-${a.id}`} />
                <IconBtn icon={KeyRound} title="Reset password" onClick={() => resetPassword(a)} testid={`btn-reset-password-${a.id}`} />
                <IconBtn icon={Power} title={a.is_active ? 'Disable' : 'Enable'} onClick={() => toggleActive(a)} testid={`btn-toggle-active-${a.id}`} />
                <IconBtn icon={Trash2} title="Delete" onClick={() => remove(a)} testid={`btn-delete-account-${a.id}`} danger />
              </div>
            </div>
          ))}
          {rows.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No accounts found</p>}
        </div>
      </CardContent>
      <AccountFormDialog open={formOpen} account={editing} roles={meta.roles} onClose={() => setFormOpen(false)} onSubmit={submit} />
      <CredentialsDialog creds={creds} onClose={() => setCreds(null)} />
    </Card>
  );
};
